import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getBillingStatus } from '../api/billing';
import { COMMERCIAL_STATUS_EVENT } from '../api/client';
import { getPendingSummary } from '../api/dashboard';
import { CommercialNotice } from '../components/billing/CommercialNotice';
import { roleLabel } from '../components/BusinessUi';
import type { BillingStatus } from '../types/billing';
import { commercialNotice } from '../utils/billingView';
import { useLoyversePresence } from '../utils/loyversePresence';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import { AppShell } from './AppShell';
import { InfoDialog } from '../info/InfoDialog';
import { appVersion, screenOf, type SupportContext } from '../info/supportInfo';
import type { AppSection } from './navConfig';
import { userDisplayNameOr } from '../utils/userDisplayName';

export type { AppSection } from './navConfig';

interface AppLayoutProps {
  activeSection: AppSection;
  children: ReactNode;
  onNavigate: (section: AppSection) => void;
}

/**
 * Fase 16B-2 / 24B: navegacion agrupada con iconos reales (config en navConfig.ts, testeable).
 * `isVisible` es solo experiencia — la seguridad real vive en backend. El filtrado por rol es
 * IDENTICO al anterior.
 *
 * Rediseño · Bloque B: lo que se dibuja es `AppShell` (menú, barra superior y barra inferior del
 * diseño aprobado). Aquí queda lo que depende de la sesión y del servidor: quién eres, tus tiendas,
 * si Loyverse está conectado y los conteos de pendientes.
 */
export function AppLayout({ activeSection, children, onNavigate }: AppLayoutProps) {
  const { business, stores, user, logout } = useAuth();
  /**
   * Quién soy y desde dónde estoy trabajando, visible en TODAS las pantallas.
   * Antes el nombre y el puesto sólo se veían en escritorio (el móvil los ocultaba por completo) y
   * la tienda no aparecía en ningún lado. En un negocio con más de una sucursal, saber en cuál estás
   * parado no es adorno: es lo que evita capturar un gasto en la tienda equivocada.
   * Con varias tiendas asignadas no se elige una por el usuario: se dice cuántas son.
   */
  const storeLabel = stores.length === 1 ? stores[0]!.name : stores.length > 1 ? `${stores.length} tiendas` : null;
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Panel de Pendientes: badges de conteo en la nav (OWNER/MANAGER). Solo lectura; nunca rompe el shell.
  const [badges, setBadges] = useState<Partial<Record<AppSection, number>>>({});

  // ZTV-04: sin integración Loyverse conectada, "Ventas Loyverse" no aparece como si fuera un
  // módulo pendiente; "Conexión Loyverse" (Configuración) sigue visible para descubrirla.
  const loyverseConnected = useLoyversePresence();

  useEffect(() => {
    if (user?.role !== 'OWNER' && user?.role !== 'MANAGER') return;
    let alive = true;
    getPendingSummary()
      .then((d) => { if (alive) setBadges({ 'catalog-approvals': d.catalogPending, 'void-requests': d.voidRequestsPending, purchases: d.purchasesPendingApproval, counts: d.countLinesToApprove ?? 0 }); })
      .catch(() => { /* los badges nunca rompen la navegación */ });
    return () => { alive = false; };
  }, [user?.role]);

  // ONB-007: estado comercial del negocio (todos los roles; lo decide el servidor con su hora). Se vuelve a
  // leer cuando el servidor niega una escritura por solo lectura o cuando «Plan y facturación» cambia algo.
  // Nunca rompe el shell: sin respuesta, simplemente no hay aviso ni destino de facturación.
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const refresh = () => {
      getBillingStatus()
        .then((next) => { if (alive) setBilling(next); })
        .catch(() => { /* el aviso de facturación nunca rompe la navegación */ });
    };
    refresh();
    window.addEventListener(COMMERCIAL_STATUS_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(COMMERCIAL_STATUS_EVENT, refresh);
    };
  }, [user?.id]);
  const notice = commercialNotice(billing, user?.role, stores[0]?.timezone ?? DEFAULT_STORE_TIMEZONE);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await logout();
  };

  // P-085: la ayuda, en un diálogo con los datos de soporte de ESTA sesión (negocio, tienda, rol y pantalla; nada secreto).
  const [helpOpen, setHelpOpen] = useState(false);
  // Codex R2-03: si la pantalla cambia (el Atrás del navegador, un enlace), la ayuda se cierra: no queda abierta encima
  // de otra pantalla. Aquí no se arma una guarda del historial: el punto de venta ya tiene la suya.
  useEffect(() => {
    if (!helpOpen) return undefined;
    const close = () => setHelpOpen(false);
    window.addEventListener('hashchange', close);
    return () => window.removeEventListener('hashchange', close);
  }, [helpOpen]);
  const helpContext = (): SupportContext => ({
    businessName: business?.name ?? null,
    businessSlug: business?.slug ?? null,
    storeLabel,
    role: user?.role ?? null,
    screen: screenOf(window.location.hash),
    version: appVersion(),
    at: new Date(),
    userAgent: navigator.userAgent,
  });

  return (
    <AppShell
      activeSection={activeSection}
      badges={badges}
      billingAvailable={billing?.enabled === true}
      businessName={business?.name || 'Negocio'}
      initials={getInitials(user?.firstName, user?.lastName, user?.username)}
      isLoggingOut={isLoggingOut}
      loyverseConnected={loyverseConnected}
      onHelp={() => setHelpOpen(true)}
      onLogout={() => void handleLogout()}
      onNavigate={onNavigate}
      role={user?.role}
      roleName={roleLabel(user?.role)}
      storeLabel={storeLabel}
      userName={userDisplayNameOr(user)}
    >
      {/* Aquí vivía el aviso de "modo piloto" (Fase 17A-3), que decía que Apps Script seguía
          siendo la fuente oficial. Dejó de ser cierto: el negocio opera en esta app desde el
          25-ago-2026. Un aviso que miente todos los días enseña a ignorar los avisos. */}
      {/* ONB-007: aviso de facturación SOLO cuando el acceso está en riesgo ahora (solo lectura o pago
          pendiente); durante el trial no se muestra nada. En «Plan y facturación» no se repite. */}
      {notice && activeSection !== 'billing' && <CommercialNotice notice={notice} onOpenBilling={() => onNavigate('billing')} />}
      {children}
      {helpOpen && <InfoDialog context={helpContext()} onClose={() => setHelpOpen(false)} />}
    </AppShell>
  );
}

function getInitials(firstName?: string | null, lastName?: string | null, username?: string): string {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  return (initials || username?.slice(0, 2) || 'CO').toUpperCase();
}
